import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { 
  NavArrowLeft, Group, CreditCard, ShieldCheck, Page, 
  DatabaseBackup, Activity, WarningTriangle, CheckCircle, Xmark 
} from 'iconoir-react';
import '../admin.css';

interface TenantDetailProps {}

const TenantDetail: React.FC<TenantDetailProps> = () => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState<any>(null);
  const [statistics, setStatistics] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'identity' | 'commercial' | 'legal' | 'gdpr' | 'storage' | 'activity'>('identity');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTenantDetails();
  }, [tenantId]);

  const loadTenantDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/tenants/${tenantId}`);
      setTenant(response.data.tenant);
      setStatistics(response.data.statistics);
    } catch (error) {
      console.error('Erreur lors du chargement des détails:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSuspend = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir suspendre cette organisation ?')) return;
    
    try {
      await api.post(`/admin/tenants/${tenantId}/suspend`);
      await loadTenantDetails();
      alert('Organisation suspendue avec succès');
    } catch (error) {
      console.error('Erreur lors de la suspension:', error);
      alert('Erreur lors de la suspension');
    }
  };

  const handleReactivate = async () => {
    try {
      await api.post(`/admin/tenants/${tenantId}/reactivate`);
      await loadTenantDetails();
      alert('Organisation réactivée avec succès');
    } catch (error) {
      console.error('Erreur lors de la réactivation:', error);
      alert('Erreur lors de la réactivation');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('ATTENTION: Cette action supprimera définitivement toutes les données de cette organisation. Êtes-vous absolument sûr ?')) return;
    if (!window.confirm('Dernière confirmation: Toutes les données seront perdues. Continuer ?')) return;
    
    try {
      await api.delete(`/admin/tenants/${tenantId}`);
      alert('Organisation supprimée avec succès');
      navigate('/admin/saas');
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleRecalculateStorage = async () => {
    try {
      await api.post(`/admin/tenants/${tenantId}/recalculate-storage`);
      await loadTenantDetails();
      alert('Stockage recalculé avec succès');
    } catch (error) {
      console.error('Erreur lors du recalcul:', error);
      alert('Erreur lors du recalcul du stockage');
    }
  };

  const handleProcessGdprRequest = async (requestId: number, status: string, notes: string) => {
    try {
      await api.put(`/admin/gdpr-requests/${requestId}`, {
        status,
        admin_notes: notes
      });
      await loadTenantDetails();
      alert('Demande RGPD traitée avec succès');
    } catch (error) {
      console.error('Erreur lors du traitement:', error);
      alert('Erreur lors du traitement de la demande RGPD');
    }
  };

  const handleMarkInvoicePaid = async (invoiceId: number) => {
    const paymentMethod = prompt('Méthode de paiement (card/sepa/transfer):');
    if (!paymentMethod) return;

    const paymentReference = prompt('Référence de paiement (optionnel):');

    try {
      await api.post(`/admin/invoices/${invoiceId}/mark-paid`, {
        payment_method: paymentMethod,
        payment_reference: paymentReference
      });
      await loadTenantDetails();
      alert('Facture marquée comme payée');
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la mise à jour de la facture');
    }
  };

  if (loading || !tenant) {
    return <div className="admin-loading">Chargement...</div>;
  }

  const storagePercent = statistics?.storage_usage_percent || 0;

  return (
    <div className="tenant-detail">
      {/* Header */}
      <div className="tenant-detail-header">
        <button onClick={() => navigate('/admin/saas')} className="btn-back">
          <NavArrowLeft width={20} height={20} />
          Retour
        </button>
        
        <div className="tenant-header-info">
          <h1>{tenant.name}</h1>
          <p className="tenant-slug">{tenant.slug}</p>
          {tenant.domain && <p className="tenant-domain">🌐 {tenant.domain}</p>}
        </div>

        <div className="tenant-header-actions">
          {tenant.is_active ? (
            <button onClick={handleSuspend} className="btn-admin-suspend">
              Suspendre
            </button>
          ) : (
            <button onClick={handleReactivate} className="btn-admin-activate">
              Réactiver
            </button>
          )}
          <button onClick={handleDelete} className="btn-admin-delete">
            Supprimer
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="tenant-tabs">
        <button 
          className={activeTab === 'identity' ? 'active' : ''}
          onClick={() => setActiveTab('identity')}
        >
          <Group width={18} height={18} />
          Identité
        </button>
        <button 
          className={activeTab === 'commercial' ? 'active' : ''}
          onClick={() => setActiveTab('commercial')}
        >
          <CreditCard width={18} height={18} />
          Commercial
        </button>
        <button 
          className={activeTab === 'legal' ? 'active' : ''}
          onClick={() => setActiveTab('legal')}
        >
          <ShieldCheck width={18} height={18} />
          Juridique
        </button>
        <button 
          className={activeTab === 'gdpr' ? 'active' : ''}
          onClick={() => setActiveTab('gdpr')}
        >
          <Page width={18} height={18} />
          RGPD
          {tenant.gdpr_requests?.filter((r: any) => r.status === 'pending').length > 0 && (
            <span className="tab-badge">{tenant.gdpr_requests.filter((r: any) => r.status === 'pending').length}</span>
          )}
        </button>
        <button 
          className={activeTab === 'storage' ? 'active' : ''}
          onClick={() => setActiveTab('storage')}
        >
          <DatabaseBackup width={18} height={18} />
          Stockage
        </button>
        <button 
          className={activeTab === 'activity' ? 'active' : ''}
          onClick={() => setActiveTab('activity')}
        >
          <Activity width={18} height={18} />
          Activité
        </button>
      </div>

      {/* Tab Content */}
      <div className="tenant-tab-content">
        {/* Identité */}
        {activeTab === 'identity' && (
          <div className="tab-panel">
            <h2>Informations de l'organisation</h2>
            
            <div className="info-grid">
              <div className="info-item">
                <label>Nom</label>
                <div>{tenant.name}</div>
              </div>
              <div className="info-item">
                <label>Slug</label>
                <div>{tenant.slug}</div>
              </div>
              <div className="info-item">
                <label>Domaine</label>
                <div>{tenant.domain || 'Non configuré'}</div>
              </div>
              <div className="info-item">
                <label>Statut</label>
                <div>
                  <span className={`badge ${tenant.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {tenant.is_active ? 'Actif' : 'Inactif'}
                  </span>
                </div>
              </div>
              <div className="info-item">
                <label>Créé le</label>
                <div>{new Date(tenant.created_at).toLocaleString('fr-FR')}</div>
              </div>
            </div>

            <h3>Utilisateurs par rôle</h3>
            <div className="stats-grid">
              {Object.entries(statistics?.users_by_role || {}).map(([role, count]: [string, any]) => (
                <div key={role} className="stat-box">
                  <div className="stat-label">{role}</div>
                  <div className="stat-value">{count}</div>
                </div>
              ))}
            </div>

            <h3>Liste des utilisateurs ({tenant.users?.length || 0})</h3>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>Email</th>
                  <th>Rôle</th>
                  <th>Inscrit le</th>
                </tr>
              </thead>
              <tbody>
                {tenant.users?.map((user: any) => (
                  <tr key={user.id}>
                    <td>{user.name}</td>
                    <td>{user.email}</td>
                    <td><span className="badge">{user.role}</span></td>
                    <td>{new Date(user.created_at).toLocaleDateString('fr-FR')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Commercial */}
        {activeTab === 'commercial' && (
          <div className="tab-panel">
            <h2>Abonnement</h2>
            
            {tenant.subscription && (
              <div className="subscription-info">
                <div className="info-grid">
                  <div className="info-item">
                    <label>Plan</label>
                    <div><span className="badge badge-lg">{tenant.subscription.plan}</span></div>
                  </div>
                  <div className="info-item">
                    <label>Prix mensuel</label>
                    <div className="price">{tenant.subscription.monthly_price} €</div>
                  </div>
                  <div className="info-item">
                    <label>Statut</label>
                    <div><span className={`badge ${tenant.subscription.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {tenant.subscription.status}
                    </span></div>
                  </div>
                  <div className="info-item">
                    <label>Cycle de facturation</label>
                    <div>{tenant.subscription.billing_cycle}</div>
                  </div>
                  <div className="info-item">
                    <label>Limites</label>
                    <div>
                      {tenant.subscription.max_users} utilisateurs<br/>
                      {tenant.subscription.max_boards} boards<br/>
                      {tenant.subscription.max_storage_mb} MB stockage
                    </div>
                  </div>
                </div>
              </div>
            )}

            <h3>Statistiques de facturation</h3>
            <div className="billing-stats">
              <div className="stat-box">
                <div className="stat-label">Revenu total</div>
                <div className="stat-value">{statistics?.billing?.total_revenue?.toFixed(2) || '0.00'} €</div>
              </div>
              <div className="stat-box">
                <div className="stat-label">En attente</div>
                <div className="stat-value">{statistics?.billing?.pending_amount?.toFixed(2) || '0.00'} €</div>
              </div>
              <div className="stat-box">
                <div className="stat-label">Paiements échoués</div>
                <div className="stat-value">{statistics?.billing?.failed_payments || 0}</div>
              </div>
            </div>

            <h3>Historique de facturation</h3>
            <table className="data-table">
              <thead>
                <tr>
                  <th>N° Facture</th>
                  <th>Date</th>
                  <th>Montant</th>
                  <th>Statut</th>
                  <th>Méthode</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tenant.invoices?.map((invoice: any) => (
                  <tr key={invoice.id}>
                    <td>{invoice.invoice_number}</td>
                    <td>{new Date(invoice.invoice_date).toLocaleDateString('fr-FR')}</td>
                    <td>{invoice.amount} {invoice.currency}</td>
                    <td>
                      <span className={`badge ${
                        invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
                        invoice.status === 'failed' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {invoice.status}
                      </span>
                    </td>
                    <td>{invoice.payment_method || '-'}</td>
                    <td>
                      {invoice.status === 'pending' && (
                        <button 
                          onClick={() => handleMarkInvoicePaid(invoice.id)}
                          className="btn-sm btn-success"
                        >
                          Marquer payé
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Juridique */}
        {activeTab === 'legal' && (
          <div className="tab-panel">
            <h2>Consentements juridiques</h2>
            
            <table className="data-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Version</th>
                  <th>Statut</th>
                  <th>Accepté le</th>
                  <th>Accepté par</th>
                  <th>IP</th>
                </tr>
              </thead>
              <tbody>
                {tenant.consents?.length > 0 ? (
                  tenant.consents.map((consent: any) => (
                    <tr key={consent.id}>
                      <td>{consent.consent_type}</td>
                      <td>{consent.version}</td>
                      <td>
                        {consent.accepted ? (
                          <CheckCircle width={20} height={20} color="#10b981" />
                        ) : (
                          <Xmark width={20} height={20} color="#ef4444" />
                        )}
                      </td>
                      <td>{consent.accepted_at ? new Date(consent.accepted_at).toLocaleString('fr-FR') : '-'}</td>
                      <td>{consent.accepted_by_user_id || '-'}</td>
                      <td>{consent.ip_address || '-'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="text-center">Aucun consentement enregistré</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* RGPD */}
        {activeTab === 'gdpr' && (
          <div className="tab-panel">
            <h2>Demandes RGPD</h2>
            
            {tenant.gdpr_requests?.length > 0 ? (
              <div className="gdpr-requests">
                {tenant.gdpr_requests.map((request: any) => (
                  <div key={request.id} className={`gdpr-request-card ${request.status}`}>
                    <div className="gdpr-header">
                      <div>
                        <h4>{request.request_type}</h4>
                        <p className="gdpr-user">{request.user?.name} ({request.user?.email})</p>
                      </div>
                      <span className={`badge ${
                        request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        request.status === 'completed' ? 'bg-green-100 text-green-800' :
                        request.status === 'rejected' ? 'bg-red-100 text-red-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {request.status}
                      </span>
                    </div>
                    
                    <div className="gdpr-body">
                      <p><strong>Description:</strong> {request.description || 'Aucune description'}</p>
                      <p><strong>Demandé le:</strong> {new Date(request.requested_at).toLocaleString('fr-FR')}</p>
                      {request.processed_at && (
                        <p><strong>Traité le:</strong> {new Date(request.processed_at).toLocaleString('fr-FR')}</p>
                      )}
                      {request.admin_notes && (
                        <p><strong>Notes admin:</strong> {request.admin_notes}</p>
                      )}
                    </div>

                    {request.status === 'pending' && (
                      <div className="gdpr-actions">
                        <button 
                          onClick={() => {
                            const notes = prompt('Notes administrateur (optionnel):');
                            handleProcessGdprRequest(request.id, 'completed', notes || '');
                          }}
                          className="btn-sm btn-success"
                        >
                          Approuver
                        </button>
                        <button 
                          onClick={() => {
                            const notes = prompt('Raison du rejet:');
                            if (notes) handleProcessGdprRequest(request.id, 'rejected', notes);
                          }}
                          className="btn-sm btn-danger"
                        >
                          Rejeter
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-data">Aucune demande RGPD</p>
            )}
          </div>
        )}

        {/* Stockage */}
        {activeTab === 'storage' && (
          <div className="tab-panel">
            <h2>Utilisation du stockage</h2>
            
            {tenant.storage && (
              <>
                <div className="storage-overview">
                  <div className="storage-stat">
                    <label>Pièces jointes</label>
                    <div className="value">{tenant.storage.attachments_size_mb.toFixed(2)} MB</div>
                    <div className="detail">{tenant.storage.attachments_count} fichiers</div>
                  </div>
                  <div className="storage-stat">
                    <label>Avatars</label>
                    <div className="value">{tenant.storage.avatars_size_mb.toFixed(2)} MB</div>
                  </div>
                  <div className="storage-stat">
                    <label>Total utilisé</label>
                    <div className="value">{tenant.storage.total_size_mb.toFixed(2)} MB</div>
                  </div>
                  <div className="storage-stat">
                    <label>Limite</label>
                    <div className="value">{tenant.subscription?.max_storage_mb || 1000} MB</div>
                  </div>
                </div>

                <div className="storage-progress">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ 
                        width: `${Math.min(storagePercent, 100)}%`,
                        background: storagePercent > 90 ? '#ef4444' : storagePercent > 75 ? '#f59e0b' : 'linear-gradient(135deg, #00bf7e 0%, #026AA7 100%)'
                      }}
                    />
                  </div>
                  <div className="progress-label">{storagePercent.toFixed(1)}% utilisé</div>
                </div>

                <button onClick={handleRecalculateStorage} className="btn-admin-secondary">
                  Recalculer le stockage
                </button>

                {tenant.storage.last_calculated_at && (
                  <p className="text-sm text-gray-500">
                    Dernière mise à jour: {new Date(tenant.storage.last_calculated_at).toLocaleString('fr-FR')}
                  </p>
                )}
              </>
            )}
          </div>
        )}

        {/* Activité */}
        {activeTab === 'activity' && (
          <div className="tab-panel">
            <h2>Journal d'activité</h2>
            
            {tenant.activity_logs?.length > 0 ? (
              <div className="activity-log">
                {tenant.activity_logs.map((log: any) => (
                  <div key={log.id} className="activity-item">
                    <div className="activity-icon">
                      <Activity width={16} height={16} />
                    </div>
                    <div className="activity-content">
                      <div className="activity-description">{log.description}</div>
                      <div className="activity-meta">
                        {log.user?.name} • {new Date(log.created_at).toLocaleString('fr-FR')}
                        {log.ip_address && ` • ${log.ip_address}`}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-data">Aucune activité enregistrée</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TenantDetail;
